import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "../lib/api";
import Header from "../components/Header";
import TrustMark from "../components/TrustMark";
import Reveal from "../components/Reveal";
import CountUp from "../components/CountUp";
import LivePreviewTrio from "../components/LivePreviewTrio";
import HeroLiveFeed from "../components/HeroLiveFeed";
import LiveActivityFeed from "../components/LiveActivityFeed";
import useAbTest from "../lib/useAbTest";
import { ArrowRight, Bell, ShieldCheck, Vault, Wrench, FileText, ArrowUp, CalendarBlank, Robot, Storefront } from "@phosphor-icons/react";

const RETAILERS = [
  { name: "PB Tech", status: "Live", live: true, slug: "pb-tech" },
  { name: "JB Hi-Fi", status: "Live", live: true, slug: "jb-hifi" },
  { name: "Harvey Norman", status: "Live", live: true, slug: "harvey-norman" },
  { name: "Noel Leeming", status: "Coming soon", live: false, slug: null },
  { name: "Mighty Ape", status: "Coming soon", live: false, slug: null },
  { name: "More retailers", status: "Unlocking as we grow", live: false, slug: null },
];

export default function Landing() {
  const [stats, setStats] = useState({ items_tracked: 0, community_savings: 0, users_count: 0 });
  useEffect(() => {
    // Try the configured backend first; if that fails (e.g. a stale
    // REACT_APP_BACKEND_URL was baked into a deployed build), fall back to a
    // same-origin call which always works in production.
    let cancelled = false;
    const apply = (data) => { if (!cancelled && data) setStats(data); };
    api.get("/public/stats")
      .then(({ data }) => apply(data))
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("[stats] primary fetch failed, trying same-origin", err?.message);
        fetch("/api/public/stats")
          .then((r) => (r.ok ? r.json() : null))
          .then(apply)
          .catch((e) => console.warn("[stats] same-origin fallback failed", e?.message));
      });
    return () => { cancelled = true; };
  }, []);

  // A/B test: hero primary CTA copy. Sticky 50/50 per visitor, GA4-tracked.
  const { variant: heroCtaVariant, track: trackHero } = useAbTest("hero_cta_v1", ["control", "wishlist"]);
  const heroCtaLabel = heroCtaVariant === "wishlist"
    ? "Wishlist your next big purchase"
    : "Start protecting your purchases";

  return (
    <div className="bg-white text-black">
      <Header />

      {/* HERO */}
      <section className="border-b border-[#e4e4e7]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-12 lg:pb-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          <div className="lg:col-span-7 pg-fade">
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95]">
              Get more from<br/>
              every purchase<span className="text-[#002FA7]">.</span>
            </h1>
            <p className="text-base sm:text-lg font-semibold text-[#0a0a0a] mt-6 max-w-xl" data-testid="hero-tagline">
              Whether you're planning a purchase, protecting a recent buy, or keeping track of receipts and warranties, PriceGuard helps you stay one step ahead.
            </p>
            <p className="text-base leading-relaxed text-[#52525b] mt-5 max-w-xl">
              Built for every Kiwi household, no matter the size of the purchase. Wishlist a $40 kettle or a $4,000 TV, claim back the difference when something drops in price not long after you bought it, and vault every receipt so we can remind you before warranty cover expires. One quiet app, working in the background for the whole household.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-10">
              <Link
                to="/signup"
                onClick={() => trackHero("ab_test_click_signup", { cta_location: "hero" })}
                className="bg-[#002FA7] text-white font-bold px-7 py-4 inline-flex items-center gap-3 hover:bg-[#00207A] transition-colors"
                data-testid="hero-cta-signup"
                data-ab-variant={heroCtaVariant}
              >
                {heroCtaLabel} <ArrowRight size={18} weight="bold" />
              </Link>
              <Link to="/login" className="border border-black font-bold px-7 py-4 inline-flex items-center justify-center hover:bg-black hover:text-white transition-colors" data-testid="hero-cta-login">
                I already have an account
              </Link>
              <Link
                to="/tour"
                onClick={() => { try { if (window.gtag) window.gtag("event", "tour_open", { source: "landing_hero" }); } catch { /* no-op */ } }}
                className="border border-[#002FA7] text-[#002FA7] font-bold px-7 py-4 inline-flex items-center justify-center gap-2 hover:bg-[#002FA7] hover:text-white transition-colors"
                data-testid="hero-cta-tour"
              >
                Take a tour first <ArrowRight size={16} weight="bold" />
              </Link>
            </div>

            <p className="text-xs font-mono-pg text-[#52525b] mt-6 uppercase tracking-widest" data-testid="hero-audience-line">
              Pro free for everyone right now · Unlimited tracking · No card needed
            </p>
          </div>

          {/* Live activity feed — fills the right column with a heartbeat. */}
          <div className="lg:col-span-5">
            <HeroLiveFeed />
          </div>
        </div>
      </section>

      {/* DON'T JUST BUY IT. PROTECT IT.
          Combined section: keeps the animated trio demo and the
          "Don't just buy it. Protect it." headline + intro. Moved up
          right under the hero so a visitor understands the whole
          product in ~10 seconds. */}
      <LivePreviewTrio />

      {/* SOCIAL PROOF STRIP */}
      <section className="border-b border-[#e4e4e7] bg-[#0a0a0a] text-white" data-testid="social-proof-strip">
        <Reveal className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <div className="pg-overline text-white/50">Price adjustment value identified</div>
            <div className="font-heading text-4xl lg:text-5xl font-black tracking-tighter mt-2" data-testid="stat-community-savings">
              <CountUp value={stats.community_savings} prefix="$" duration={1600} decimals={0} />
            </div>
            <div className="text-sm text-white/60 mt-1">across NZ retailers, surfaced for our members</div>
          </div>
          <div>
            <div className="pg-overline text-white/50">Products under active protection</div>
            <div className="font-heading text-4xl lg:text-5xl font-black tracking-tighter mt-2" data-testid="stat-items-tracked">
              <CountUp value={stats.items_tracked} duration={1200} />
            </div>
            <div className="text-sm text-white/60 mt-1">tracked daily before and after purchase</div>
          </div>
          <div>
            <div className="pg-overline text-white/50">Kiwi members shopping smarter</div>
            <div className="font-heading text-4xl lg:text-5xl font-black tracking-tighter mt-2" data-testid="stat-users">
              <CountUp value={stats.users_count} duration={1400} />
            </div>
            <div className="text-sm text-white/60 mt-1">using PriceGuard for purchase confidence</div>
          </div>
        </Reveal>
      </section>

      {/* LIVE ACTIVITY FEED - prominent, anonymised proof of work happening
          across the user base. Big colourful tiles + recent drop ticker. */}
      <LiveActivityFeed variant="landing" />

      {/* LIVE PREVIEW TRIO - now lives inside the hero section above. */}

      {/* WHY RETAILERS LIKE US */}
      <section className="border-b border-[#e4e4e7] bg-white" data-testid="retailer-partnership-section">
        <Reveal className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <h2 className="font-heading text-3xl lg:text-4xl font-extrabold tracking-tight">PriceGuard works with retailers, not against them.</h2>
          </div>
          <div className="lg:col-span-7 space-y-6">
            <p className="text-[#52525b] leading-relaxed">
              Major NZ retailers already publish price-protection and price-match policies. PriceGuard simply makes them easier to use - surfacing the exact pricing intelligence a customer needs to ask for what they're already entitled to, in the wording the retailer prefers.
            </p>
            <p className="text-[#52525b] leading-relaxed">
              The result: shoppers keep the products they bought, retailers keep the sale and the customer, and the relationship stays positive on both sides. PriceGuard becomes the trust layer between the two.
            </p>
            <div className="pt-2">
              <TrustMark />
            </div>
          </div>
        </Reveal>
      </section>

      {/* RETAILERS */}
      <section id="retailers" className="border-b border-[#e4e4e7] bg-[#f7f7f8]">
        <Reveal className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
          <div className="max-w-3xl mb-10">
            <h2 className="font-heading text-3xl lg:text-4xl font-extrabold tracking-tight">Working alongside NZ's leading electronics chains.</h2>
            <p className="text-[#52525b] mt-4 leading-relaxed">From kettles to TVs - if it has a price online at a supported retailer, PriceGuard can watch it. We're adding more retailers as we grow.</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-[#e4e4e7] border border-[#e4e4e7]">
            {RETAILERS.map((r) => {
              const cardClass = `${r.live ? "bg-white" : "bg-[#f0f0f2]"} py-8 px-4 text-center pg-lift`;
              const testid = `retailer-${r.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
              const inner = (
                <>
                  <div className={`font-heading font-bold tracking-tight ${r.live ? "" : "text-[#52525b]"}`}>{r.name}</div>
                  <div className="pg-overline mt-1 text-[10px]">{r.status}</div>
                </>
              );
              return r.slug ? (
                <Link key={r.name} to={`/track/${r.slug}`} className={cardClass} data-testid={testid}>
                  {inner}
                </Link>
              ) : (
                <div key={r.name} className={cardClass} data-testid={testid}>
                  {inner}
                </div>
              );
            })}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-4 mt-6">
            <p className="text-xs text-[#52525b] font-mono-pg">Independent service. PriceGuard NZ is not affiliated with any retailer listed above.</p>
            <Link to="/retailers" className="inline-flex items-center gap-1.5 font-semibold text-[#002FA7] hover:text-[#00207A] text-sm" data-testid="landing-see-all-retailers-link">
              See all supported retailers <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        </Reveal>
      </section>

      {/* COMING NEXT - VAULT ROADMAP TEASER (full grid lives on /about) */}
      <section id="vault" className="border-b border-[#e4e4e7] bg-white" data-testid="vault-roadmap-section">
        <Reveal className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <div className="pg-overline mb-4 text-[#16A34A]">Live + shipping next</div>
              <h2 className="font-heading text-3xl lg:text-4xl font-extrabold tracking-tight">
                A vault for everything<br/>your household owns<span className="text-[#002FA7]">.</span>
              </h2>
              <p className="text-[#52525b] leading-relaxed mt-5">
                Receipt vault, warranty alerts, and AI-drafted warranty claims are live now. Insurance-ready exports, resale value tracking, year-in-review, subscription drift, and back-in-stock alerts are rolling out over the months ahead.
              </p>
              <Link to="/about" className="inline-flex items-center gap-2 mt-6 font-bold text-[#0a0a0a] border-b-2 border-[#0a0a0a] hover:text-[#002FA7] hover:border-[#002FA7] pb-0.5" data-testid="landing-roadmap-link">
                See the full roadmap <ArrowRight size={14} weight="bold" />
              </Link>
            </div>
            <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-4 gap-px bg-[#e4e4e7] border border-[#e4e4e7]">
              {[
                { icon: Vault, t: "Receipt Vault", live: true },
                { icon: Wrench, t: "Warranty alerts", live: true },
                { icon: Robot, t: "AI warranty claims", live: true },
                { icon: FileText, t: "Insurance schedule" },
                { icon: ArrowUp, t: "Resale value tracker" },
                { icon: CalendarBlank, t: "Year-in-review" },
                { icon: Bell, t: "Subscription drift" },
                { icon: Storefront, t: "Back-in-stock alerts" },
              ].map((f, idx) => (
                <div key={f.t} className="bg-white p-5 relative" data-testid={`vault-feature-${idx}`}>
                  <span className={`absolute top-2.5 right-2.5 text-[8px] font-bold tracking-widest px-1 py-0.5 uppercase ${f.live ? "bg-[#16A34A] text-white" : "bg-[#e4e4e7] text-[#52525b]"}`} data-testid={`vault-feature-pill-${idx}`}>
                    {f.live ? "Live" : "Soon"}
                  </span>
                  <f.icon size={22} weight="duotone" color={f.live ? "#002FA7" : "#52525b"} />
                  <h3 className="font-heading text-xs font-bold tracking-tight mt-3 pr-8 leading-tight">{f.t}</h3>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      {/* PRICING TEASER — full plan tiles live on /pricing. */}
      <section id="pricing" className="border-b border-[#e4e4e7]">
        <Reveal className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <h2 className="font-heading text-3xl lg:text-4xl font-extrabold tracking-tight">
                Made for households.<br/>Built for businesses too.
              </h2>
              <p className="text-[#52525b] mt-5 leading-relaxed max-w-md">
                Start free, upgrade when you outgrow it, and bring your business along when you're ready. One PriceGuard, three ways to use it.
              </p>
              <p className="text-[#52525b] mt-3 leading-relaxed max-w-md font-mono-pg text-xs uppercase tracking-widest">
                Pro is free for everyone during launch · no card needed
              </p>
              <div className="flex flex-wrap gap-3 mt-7">
                <Link to="/signup" className="bg-[#002FA7] text-white font-bold px-6 py-3 inline-flex items-center gap-2 hover:bg-[#00207A]" data-testid="landing-pricing-signup-cta">
                  Sign up, Pro included free <ArrowRight size={14} weight="bold" />
                </Link>
                <Link to="/pricing" className="border border-black font-bold px-6 py-3 inline-flex items-center gap-2 hover:bg-black hover:text-white transition-colors" data-testid="landing-pricing-detail-cta">
                  See full pricing
                </Link>
              </div>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#e4e4e7] border border-[#e4e4e7]">
              <div className="bg-white p-7 flex flex-col" data-testid="landing-plan-free">
                <div className="pg-overline">Free · no card needed</div>
                <h3 className="font-heading text-xl font-extrabold mt-3 tracking-tight">Household starter</h3>
                <div className="font-heading text-4xl font-black mt-5 tracking-tighter">$0</div>
                <p className="font-mono-pg text-[10px] text-[#52525b] mt-1 uppercase tracking-widest">A real taste before you commit</p>
                <ul className="mt-5 space-y-2 text-sm text-[#0a0a0a] leading-relaxed">
                  <li className="flex gap-2"><ShieldCheck size={16} color="#16A34A" weight="fill" className="shrink-0 mt-0.5" /> Watch a handful of items, daily</li>
                  <li className="flex gap-2"><ShieldCheck size={16} color="#16A34A" weight="fill" className="shrink-0 mt-0.5" /> Price-drop and sale alerts by email</li>
                  <li className="flex gap-2"><ShieldCheck size={16} color="#16A34A" weight="fill" className="shrink-0 mt-0.5" /> Promo codes spotted on the page surfaced for you</li>
                  <li className="flex gap-2"><ShieldCheck size={16} color="#16A34A" weight="fill" className="shrink-0 mt-0.5" /> One refund a year, on the house</li>
                </ul>
              </div>
              <div className="bg-[#0a0a0a] text-white p-7 flex flex-col" data-testid="landing-plan-pro">
                <div className="pg-overline text-white/60">Pro household</div>
                <h3 className="font-heading text-xl font-extrabold mt-3 tracking-tight">Pro</h3>
                <div className="font-heading text-4xl font-black mt-5 tracking-tighter">
                  $4.99<span className="text-sm font-bold text-white/60 ml-1">/mo</span>
                </div>
                <p className="text-[10px] text-white/40 mt-1 font-mono-pg uppercase tracking-widest">Free during launch · or $49/yr</p>
                <ul className="mt-5 space-y-2 text-sm text-white leading-relaxed">
                  <li className="flex gap-2"><ShieldCheck size={16} color="#fff" weight="fill" className="shrink-0 mt-0.5" /> Track everything you own, without limits</li>
                  <li className="flex gap-2"><ShieldCheck size={16} color="#fff" weight="fill" className="shrink-0 mt-0.5" /> Instant alerts on price drops, sales, promo codes and bonus offers</li>
                  <li className="flex gap-2"><ShieldCheck size={16} color="#fff" weight="fill" className="shrink-0 mt-0.5" /> Refunds spotted and drafted for you</li>
                  <li className="flex gap-2"><ShieldCheck size={16} color="#fff" weight="fill" className="shrink-0 mt-0.5" /> Receipts safe, searchable, forever</li>
                  <li className="flex gap-2"><ShieldCheck size={16} color="#fff" weight="fill" className="shrink-0 mt-0.5" /> Warranties watched in the background</li>
                </ul>
              </div>
              <div className="bg-gradient-to-br from-[#002FA7] to-[#001A5C] text-white p-7 relative flex flex-col" data-testid="landing-plan-business">
                <div className="absolute top-4 right-4 text-[10px] font-bold tracking-widest uppercase bg-white/15 backdrop-blur-sm px-2 py-0.5">
                  Soon
                </div>
                <div className="pg-overline text-white/60">For businesses</div>
                <h3 className="font-heading text-xl font-extrabold mt-3 tracking-tight">Business</h3>
                <div className="font-heading text-3xl font-black mt-5 tracking-tighter">
                  Built for your books<span className="text-[#D97706]">.</span>
                </div>
                <p className="text-sm text-white/90 leading-relaxed mt-4 flex-1">
                  Everything Pro can do, applied across your team, your suppliers, and your books. Warranty tracking, an automatic asset register, smarter buying, Xero and MYOB sync, spend insights, and team access - so nothing the business owns ever slips through the cracks again.
                </p>
                <Link to="/pricing#business-deep-dive" className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-white hover:text-[#D97706] transition-colors" data-testid="landing-business-cta">
                  Register your interest <ArrowRight size={14} weight="bold" />
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* LEARN MORE STRIP */}
      <section className="border-b border-[#e4e4e7] bg-[#f7f7f8]" data-testid="learn-more-strip">
        <Reveal className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#e4e4e7] border border-[#e4e4e7]">
            <Link to="/about" className="bg-white p-8 hover:bg-white group transition-colors" data-testid="learn-about-cta">
              <h3 className="font-heading text-xl lg:text-2xl font-bold tracking-tight">A note from the team, and how we operate</h3>
              <p className="text-sm text-[#52525b] mt-2 leading-relaxed">Independent, transparent, retailer-respectful. Plus the full Receipt Vault roadmap.</p>
              <span className="inline-flex items-center gap-2 mt-4 font-semibold text-[#002FA7] group-hover:gap-3 transition-all">
                Read more <ArrowRight size={14} weight="bold" />
              </span>
            </Link>
            <Link to="/faq" className="bg-white p-8 hover:bg-white group transition-colors" data-testid="learn-faq-cta">
              <h3 className="font-heading text-xl lg:text-2xl font-bold tracking-tight">Every question Kiwis ask before signing up</h3>
              <p className="text-sm text-[#52525b] mt-2 leading-relaxed">How tracking works, what's free vs Pro, your data, the lot, in plain English.</p>
              <span className="inline-flex items-center gap-2 mt-4 font-semibold text-[#002FA7] group-hover:gap-3 transition-all">
                See answers <ArrowRight size={14} weight="bold" />
              </span>
            </Link>
          </div>
        </Reveal>
      </section>

      <footer className="py-10 px-6 lg:px-10 border-t border-[#e4e4e7]">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-4 gap-8 text-sm text-[#52525b]">
          <div className="flex flex-col gap-3">
            <TrustMark />
            <div className="font-mono-pg text-xs">© 2026 PriceGuard NZ</div>
            <div className="font-mono-pg text-xs">Christchurch, New Zealand</div>
          </div>
          <div className="flex flex-col gap-2">
            <div className="pg-overline mb-1">Product</div>
            <Link to="/pricing" className="hover:text-black" data-testid="footer-pricing">Pricing</Link>
            <Link to="/about" className="hover:text-black" data-testid="footer-about">About</Link>
            <Link to="/faq" className="hover:text-black" data-testid="footer-faq">FAQ</Link>
            <a href="#how" className="hover:text-black">How it works</a>
            <a href="#retailers" className="hover:text-black">Retailers</a>
          </div>
          <div className="flex flex-col gap-2">
            <div className="pg-overline mb-1">Save money on…</div>
            <Link to="/save/wishlist" className="hover:text-black" data-testid="footer-save-wishlist">Wishlist price drops</Link>
            <Link to="/save/price-claim" className="hover:text-black" data-testid="footer-save-price-claim">Price-adjustment refunds</Link>
            <Link to="/save/warranty" className="hover:text-black" data-testid="footer-save-warranty">Warranty claims (CGA)</Link>
          </div>
          <div className="flex flex-col gap-2">
            <div className="pg-overline mb-1">Legal</div>
            <Link to="/privacy" className="hover:text-black" data-testid="footer-privacy">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-black" data-testid="footer-terms">Terms of Service</Link>
            <a href="mailto:support@priceguard.nz" className="hover:text-black" data-testid="footer-support">support@priceguard.nz</a>
          </div>
        </div>
        <div className="max-w-[1400px] mx-auto mt-8 pt-6 border-t border-[#e4e4e7] font-mono-pg text-xs text-[#52525b] leading-relaxed max-w-3xl space-y-2">
          <p>Independent service. Not affiliated with, endorsed by, or partnered with any retailer named on this site.</p>
          <p>Prices shown are sourced from publicly available retailer pages once daily and may not reflect the most current listing. Always verify on the retailer's website before claiming. Any price-protection adjustment is at the retailer's discretion under their own published policy.</p>
          <p>Aligned with the NZ Fair Trading Act 1986, Consumer Guarantees Act 1993, and Privacy Act 2020.</p>
        </div>
      </footer>
    </div>
  );
}
